import {
  Form,
  FormField,
  FormSubmitHandler,
  OptionTypeBase,
  showNotification,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useUpdateDiscountApplicationMethodMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {APPLICATION_METHOD_OPTIONS} from '../constants/applicationMethodOptions';
import {
  discountApplicationMethodFormSchema,
  DiscountApplicationMethodFormValues,
} from './discountApplicationMethodFormSchema';
import {DiscountValidityList} from './DiscountValidityList';

export interface DiscountApplicationMethodFormProps extends RequiredTestIdProps {
  defaultValues: DiscountApplicationMethodFormValues;
  roundStepOptions: OptionTypeBase<string>[];
}

export function DiscountApplicationMethodForm(props: DiscountApplicationMethodFormProps) {
  const [updateDiscountApplicationMethod] = useUpdateDiscountApplicationMethodMutation();

  const handleSubmit: FormSubmitHandler<DiscountApplicationMethodFormValues> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    await updateDiscountApplicationMethod(values)
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSaved'));
        reset(undefined, {keepValues: true});
      })
      .catch((error: ApiError) =>
        handleApiError(error, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <Form<DiscountApplicationMethodFormValues>
      experimentalZodSchema={discountApplicationMethodFormSchema}
      defaultValues={props.defaultValues}
      onSubmit={handleSubmit}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi) => (
        <>
          <SettingsSection>
            <VStack spacing={6}>
              <FormField
                control={control}
                type="radio"
                name="applicationMethod"
                isRequired
                direction="column"
                spacing={3}
                options={APPLICATION_METHOD_OPTIONS}
                label={i18n.t('page.accountingSettings.labels.discountApplicationMethod')}
                tooltip={i18n.t('entity.accounting.labels.discountApplicationMethodTooltip')}
                data-testid={suffixTestId('applicationMethod', props)}
              />
              <DiscountValidityList
                control={control}
                formApi={formApi}
                roundStepOptions={props.roundStepOptions}
                data-testid={suffixTestId('validities', props)}
              />
            </VStack>
          </SettingsSection>
          <SettingsFooter
            data-testid={suffixTestId('footer', props)}
            actions={[
              {
                type: 'button',
                variant: 'secondary',
                title: i18n.t('general.actions.discardChanges'),
                onClick: () => formApi.reset(props.defaultValues),
                'data-testid': suffixTestId('actions.discard', props),
              },
              {
                type: 'form-button',
                control,
                buttonType: 'submit',
                variant: 'primary',
                title: i18n.t('general.actions.saveChanges'),
                'data-testid': suffixTestId('actions.submit', props),
              },
            ]}
          />
        </>
      )}
    </Form>
  );
}
